import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api, { num, money } from "@/lib/api";
import { Card, PageHeader, KPICard, HealthBadge, Modal } from "@/components/admin/ui";
import WorldMap from "@/components/admin/WorldMap";

function CityDrawer({ cityId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (cityId) api.get(`/dashboard/city/${cityId}`).then((r) => setData(r.data)).catch(() => {});
    else setData(null);
  }, [cityId]);
  if (!cityId) return null;
  const m = data?.metrics || {};
  return (
    <Modal open={!!cityId} onClose={onClose} title={data ? `${data.city.name} · City Operations` : "Loading…"} wide>
      {data ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <KPICard label="Workers" value={num(data.worker_count)} />
            <KPICard label="Teams" value={num(data.team_count)} accent="#3B82F6" />
            <KPICard label="Leads" value={num(m.leads)} accent="#06B6D4" />
            <KPICard label="Revenue" value={money(m.revenue)} accent="#10B981" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <KPICard label="Tasks" value={num(m.tasks_assigned)} />
            <KPICard label="Completed" value={`${m.completion_rate}%`} accent="#10B981" />
            <KPICard label="Meetings" value={num(m.meetings)} accent="#8B5CF6" />
            <KPICard label="Sales" value={num(m.sales)} accent="#1D4ED8" />
          </div>
          <div>
            <h4 className="font-head font-semibold text-white mb-2">Teams in {data.city.name}</h4>
            <div className="space-y-2">
              {data.teams.map((t) => (
                <div key={t.id} className="flex items-center justify-between px-4 py-2.5 rounded-lg bg-[#0B0F19] border border-white/10">
                  <span className="text-slate-200 text-sm">{t.name}</span>
                  <span className="text-slate-500 text-xs">{t.status}</span>
                </div>
              ))}
              {data.teams.length === 0 && <p className="text-slate-500 text-sm">No teams in this city yet.</p>}
            </div>
          </div>
        </div>
      ) : <p className="text-slate-400">Loading city data…</p>}
    </Modal>
  );
}

export default function GlobalOps() {
  const [cities, setCities] = useState([]);
  const [params, setParams] = useSearchParams();
  const selected = params.get("city");

  useEffect(() => { api.get("/dashboard/cities").then((r) => setCities(r.data.items)).catch(() => {}); }, []);
  const chartData = cities.map((c) => ({ name: c.name, workers: c.total_workers, revenue: c.revenue }));

  return (
    <div className="space-y-6">
      <PageHeader title="Global Operations" subtitle="Live city-level workforce activity. Click any city to drill down." />
      <WorldMap cities={cities} onSelect={(c) => setParams({ city: c.city_id })} />

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-5">
          <h3 className="font-head font-semibold text-white mb-4">Workers by city</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="name" stroke="#64748B" fontSize={10} interval={0} angle={-25} textAnchor="end" height={60} />
              <YAxis stroke="#64748B" fontSize={11} />
              <Tooltip contentStyle={{ background: "#151D2A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} />
              <Bar dataKey="workers" fill="#1D4ED8" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-5">
          <h3 className="font-head font-semibold text-white mb-4">City health board</h3>
          <div className="space-y-2 max-h-[260px] overflow-y-auto cc-scroll">
            {cities.map((c) => (
              <button key={c.city_id} onClick={() => setParams({ city: c.city_id })} className="w-full flex items-center justify-between px-4 py-2.5 rounded-lg bg-[#0B0F19] border border-white/10 hover:border-[#1D4ED8]/40">
                <span className="text-slate-200 text-sm font-medium">{c.name}</span>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-500">{num(c.total_workers)}w · {c.online_workers} online</span>
                  <HealthBadge health={c.health} />
                </div>
              </button>
            ))}
          </div>
        </Card>
      </div>

      <CityDrawer cityId={selected} onClose={() => setParams({})} />
    </div>
  );
}
